import { Directive, TemplateRef, inject, input } from '@angular/core';
import type { DocumentItem } from '@document-explorer/core';

/** The context handed to a per-column cell template. */
export interface ColumnTemplateContext {
  $implicit: DocumentItem;
  item: DocumentItem;
  /** The resolved value for this column, already formatted. */
  value: string;
}

/**
 * Marks an `ng-template` as the renderer for one column's cells — the Angular
 * equivalent of React's `ColumnDef.render`.
 *
 * ```html
 * <doc-explorer [data]="docs" [columns]="columns">
 *   <ng-template deColumn="metadata.owner" let-item let-value="value">
 *     <span class="de-avatar">{{ initials(value) }}</span>
 *     <span class="de-avatar-label">{{ value }}</span>
 *   </ng-template>
 * </doc-explorer>
 * ```
 *
 * A named `contentChild` cannot express "one template per arbitrary column
 * key", so columns are matched by the directive's input instead.
 */
@Directive({
  selector: 'ng-template[deColumn]',
  standalone: true,
})
export class DeColumnTemplateDirective {
  /** The column key this template renders, e.g. `'name'` or `'metadata.owner'`. */
  readonly deColumn = input.required<string>();

  readonly template = inject<TemplateRef<ColumnTemplateContext>>(TemplateRef);

  /** Lets Angular infer the context type inside the template. */
  static ngTemplateContextGuard(
    _directive: DeColumnTemplateDirective,
    _context: unknown,
  ): _context is ColumnTemplateContext {
    return true;
  }
}
